import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { APP_STYLING, CONTENT_STYLING } from 'constants';
import {
  BookAppearanceValuesContext,
  ReaderSettingsModifiersContext,
  ReaderSettingsValuesContext,
} from 'contexts';
import { getLocalSettingsProxy } from 'utilities';

/**
 * This provider holds settings related to the reader application, such as highlight color scheme.
 * For book appearance settings, use the book appearance provider.
 */
export const ReaderSettingsProvider = ({ children }) => {
  /**
   * For now, this depends on the background color from the book appearance context.
   *
   * If we need to reverse this dependency (i.e. book appearance depends on reader settings)
   * then we can save some constant/enum at this level and compute the blended RGB lower (like in a component).
   */

  const [localSettings] = useState(getLocalSettingsProxy);

  const { theme } = useContext(BookAppearanceValuesContext);

  const [palette, _setPalette] = useState(
    APP_STYLING.OPTIONS.COLOR_PALETTES[APP_STYLING.DEFAULTS.COLOR_PALETTE],
  );

  const setPalette = useCallback(
    (paletteId) => {
      const paletteIdOptions = Object.keys(
        APP_STYLING.OPTIONS.COLOR_PALETTE_OPTIONS,
      );
      if (paletteIdOptions.includes(paletteId)) {
        localSettings.appStyling.colorPalette = paletteId;
        _setPalette(APP_STYLING.OPTIONS.COLOR_PALETTES[paletteId]);
      }
    },
    [localSettings],
  );

  useEffect(() => {
    switch (theme) {
      case CONTENT_STYLING.OPTIONS.THEME_HIGH_CONTRAST:
        return setPalette(
          APP_STYLING.OPTIONS.COLOR_PALETTE_OPTIONS.HIGH_CONTRAST,
        );
      case CONTENT_STYLING.OPTIONS.THEME_SEPIA:
        return setPalette(APP_STYLING.DEFAULTS.COLOR_PALETTE);
      case CONTENT_STYLING.OPTIONS.THEME_NIGHT:
        return setPalette(APP_STYLING.DEFAULTS.COLOR_PALETTE);
      default:
        return setPalette(APP_STYLING.DEFAULTS.COLOR_PALETTE);
    }
  }, [theme, setPalette]);

  const values = useMemo(() => ({ ...palette }), [palette]);

  const modifiers = useMemo(
    () => ({
      setPalette,
      resetReaderStyling: setPalette(APP_STYLING.DEFAULTS.COLOR_PALETTE),
    }),
    [setPalette],
  );

  return (
    <ReaderSettingsValuesContext.Provider value={values}>
      <ReaderSettingsModifiersContext.Provider value={modifiers}>
        {children}
      </ReaderSettingsModifiersContext.Provider>
    </ReaderSettingsValuesContext.Provider>
  );
};

export default ReaderSettingsProvider;
